import type { MutableRefObject } from 'react'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import SearchFormFieldGroup from 'components/SearchV2/Components/SearchFormField/SearchFormFieldGroup'
import LineArrowsSwapHorizontalIcon from 'LuxKit/Icons/line/LineArrowsSwapHorizontalIcon'
import FlightSearchAirportSelect from './FlightAirportSelect/FlightSearchAirportSelect'
import IconButton from 'LuxKit/Button/IconButton'
import clsx from 'clsx'
import { rem } from 'polished'
import { isAgentHubCharterAirport, isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'
import { useAppSelector } from 'hooks/reduxHooks'

const InputContainer = styled(SearchFormFieldGroup)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  position: relative;

  .arrival-select {
    &--read-only{
      padding-left: ${rem(32)};
    }
    
    .display-field,  input {
      padding-left: ${rem(32)};
    }
  }
`

const ExchangeButtonContainer = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
`

interface Props {
  className?: string
  readOnly?: boolean
  id: string
  onArrivalChange?: (airport: App.AirportLocation) => void
  onDepartureChange?: (airport: App.AirportLocation) => void
  arrivalFieldRef?: MutableRefObject<HTMLInputElement | null> | ((node: HTMLInputElement) => void)
  departingFieldRef?: MutableRefObject<HTMLInputElement | null> | ((node: HTMLInputElement) => void)
  departingRecentAirports?: Array<App.AirportLocation>
  groupAirportDropdowns?: boolean
}

function FlightSearchAirportInputs(props: Props) {
  const { className, readOnly, id, onArrivalChange, onDepartureChange, departingFieldRef, arrivalFieldRef, departingRecentAirports, groupAirportDropdowns } = props
  const intl = useIntl()
  const state = useContext(FlightSearchWidgetStateContext)
  const dispatch = useContext(FlightSearchWidgetDispatchContext)
  const inputContainerRef = useRef<HTMLDivElement>(null)

  const popularAirports = useAppSelector(state => state.flights.popularAirports)

  const currentFlight = useMemo(() => {
    return state.flights.find((flight) => flight.id === id)
  }, [state.flights, id])

  const departureAirport = currentFlight?.departureAirport
  const arrivalAirport = currentFlight?.arrivalAirport

  const preSelectedDepartureAirport = useMemo(() => {
    if (isAgentHubEnabled) {
      if (departureAirport && isAgentHubCharterAirport(departureAirport)) {
        return departureAirport
      }

      return popularAirports.origins.find(airport => airport.airportCode === 'MEL')
    }
  }, [popularAirports, departureAirport])

  const preSelectedArrivalAirport = useMemo(() => {
    if (isAgentHubEnabled) {
      if (arrivalAirport && isAgentHubCharterAirport(arrivalAirport)) {
        return arrivalAirport
      }

      return popularAirports.destinations.find(airport => airport.airportCode === 'MLE')
    }
  }, [popularAirports, arrivalAirport])

  const onSwapAirports = useCallback(() => {
    const departure = departureAirport
    dispatch({ type: FlightSearchWidgetActions.SET_DEPARTURE_AIRPORT, id, airport: arrivalAirport })
    dispatch({ type: FlightSearchWidgetActions.SET_ARRIVAL_AIRPORT, id, airport: departure })

    // since agent hub has some weekdays disabled, we need to clear the dates to ensure the dates are valid
    if (isAgentHubEnabled) {
      dispatch({ type: FlightSearchWidgetActions.SET_CHECKIN_DATE, id, date: undefined })
      dispatch({ type: FlightSearchWidgetActions.SET_CHECKOUT_DATE, id, date: undefined })
    }
  }, [departureAirport, dispatch, arrivalAirport, id])

  const onDepartureSelect = useCallback((airport: App.AirportLocation) => {
    dispatch({ type: FlightSearchWidgetActions.SET_DEPARTURE_AIRPORT, id, airport })
    if (isAgentHubEnabled) {
      dispatch({ type: FlightSearchWidgetActions.SET_CHECKIN_DATE, id, date: undefined })
      dispatch({ type: FlightSearchWidgetActions.SET_CHECKOUT_DATE, id, date: undefined })
    }
    onDepartureChange?.(airport)
  }, [dispatch, id, onDepartureChange])

  const onArrivalSelect = useCallback((airport: App.AirportLocation) => {
    dispatch({ type: FlightSearchWidgetActions.SET_ARRIVAL_AIRPORT, id, airport })
    onArrivalChange?.(airport)
    if (isAgentHubEnabled) {
      dispatch({ type: FlightSearchWidgetActions.SET_CHECKIN_DATE, id, date: undefined })
      dispatch({ type: FlightSearchWidgetActions.SET_CHECKOUT_DATE, id, date: undefined })
    }
  }, [dispatch, id, onArrivalChange])

  const fromLabel = useMemo(
    () =>
      intl.formatMessage({
        id: 'ZGF6ICItXlqbkxqPyUHc',
        defaultMessage: 'From',
        description: 'Label for departure airport field in flight search',
      }),
    [intl],
  )

  const toLabel = useMemo(
    () =>
      intl.formatMessage({
        id: '2UOCctw7EHd0XUmxtgr3',
        defaultMessage: 'To',
        description: 'Form field label for arrival airport field in flight search',
      }),
    [intl],
  )

  const recentSearchHeading = useMemo(
    () =>
      departingRecentAirports
        ? intl.formatMessage({
            id: 'CZnlPtv3n6gX-dddYKi2',
            defaultMessage: 'Recent search',
            description: 'Heading for recent airport searches in flight search',
          })
        : undefined,
    [intl, departingRecentAirports],
  )

  useEffect(() => {
    if (isAgentHubEnabled) {
      preSelectedDepartureAirport && dispatch({ type: FlightSearchWidgetActions.SET_DEPARTURE_AIRPORT, id, airport: preSelectedDepartureAirport })
      preSelectedArrivalAirport && dispatch({ type: FlightSearchWidgetActions.SET_ARRIVAL_AIRPORT, id, airport: preSelectedArrivalAirport })
    }
  }, [preSelectedDepartureAirport, preSelectedArrivalAirport, dispatch, id])

  return (
    <InputContainer className={className} ref={inputContainerRef}>
      <FlightSearchAirportSelect
        label={fromLabel}
        required
        direction="departure"
        onSelectAirport={onDepartureSelect}
        airport={departureAirport}
        alternateAirport={arrivalAirport}
        readOnly={Boolean(readOnly || preSelectedDepartureAirport)}
        ref={departingFieldRef}
        fallbackAirports={departingRecentAirports}
        fallbackHeading={recentSearchHeading}
        dropdownAnchorRef={groupAirportDropdowns ? inputContainerRef : undefined}
      />

      <FlightSearchAirportSelect
        label={toLabel}
        required
        direction="arrival"
        onSelectAirport={onArrivalSelect}
        airport={arrivalAirport}
        alternateAirport={departureAirport}
        readOnly={Boolean(readOnly || preSelectedArrivalAirport)}
        ref={arrivalFieldRef}
        className={clsx('arrival-select', {
          'arrival-select--read-only': readOnly,
        })}
        dropdownAnchorRef={groupAirportDropdowns ? inputContainerRef : undefined}
      />

      <ExchangeButtonContainer className="swap">
        <IconButton variant="dark" kind="secondary" size="small" shape="circle" onClick={onSwapAirports} disabled={readOnly}>
          <LineArrowsSwapHorizontalIcon size="S" />
        </IconButton>
      </ExchangeButtonContainer>
    </InputContainer>
  )
}

export default FlightSearchAirportInputs
